import { reactive } from 'vue'

/**
 * Italian labels for the enumerated values the API stores in English.
 *
 * They live in one place because the same value shows up in a list column, a
 * detail row and a filter, and each spot used to render it its own way —
 * `text-transform: capitalize` on the raw value, which produced "Manual" and
 * "Customer" in an otherwise Italian interface.
 */

/**
 * Legacy customer types still stored on older services. Must match the
 * backend's single alias table, `CustomerType._missing_` in
 * src/models/customer.py: that is what billing uses to pick a listino, so a
 * different reading here showed "Interno" and "nessun listino" on services the
 * backend prices as Infragruppo.
 */
const CUSTOMER_TYPE_ALIASES: Record<string, string> = {
  infragruppo: 'intercompany',
  internal_dept: 'intercompany',
  // Older vocabulary for a customer; both the services and the accounts areas
  // already read it that way.
  external: 'customer'
}

export const normalizeCustomerType = (type: string | null | undefined): string =>
  CUSTOMER_TYPE_ALIASES[String(type ?? '').toLowerCase()] ?? String(type ?? '').toLowerCase()

const CUSTOMER_TYPE_LABELS: Record<string, string> = {
  customer: 'Cliente',
  internal: 'Interno',
  // "Infragruppo" è come le società del gruppo si chiamano qui dentro;
  // `intercompany` resta il valore memorizzato.
  intercompany: 'Infragruppo'
}

export const customerTypeLabel = (type: string | null | undefined): string => {
  const key = normalizeCustomerType(type)
  return CUSTOMER_TYPE_LABELS[key] || (type ? String(type) : '-')
}

/** How a service decides which resources belong to it. */
const ASSIGNMENT_STRATEGY_LABELS: Record<string, string> = {
  manual: 'Manuale',
  automatic: 'Automatica',
  hybrid: 'Ibrida'
}

export const assignmentStrategyLabel = (strategy: string | null | undefined): string => {
  const key = String(strategy ?? '').toLowerCase()
  return ASSIGNMENT_STRATEGY_LABELS[key] || (strategy ? String(strategy) : '-')
}

/** How one resource ended up assigned to a service. */
const ASSIGNMENT_ORIGIN_LABELS: Record<string, string> = {
  manual: 'Manuale',
  automatic: 'Da ruleset',
  ruleset: 'Da ruleset',
  hybrid: 'Mista'
}

export const assignmentOriginLabel = (origin: string | null | undefined): string => {
  const key = String(origin ?? '').toLowerCase()
  return ASSIGNMENT_ORIGIN_LABELS[key] || (origin ? String(origin) : '-')
}

/** What kind of thing an infrastructure asset is. */
const ASSET_TYPE_LABELS: Record<string, string> = {
  physical_host: 'Host fisico',
  cluster: 'Cluster',
  cloud_tenant: 'Tenant cloud',
  appliance: 'Appliance'
}

export const assetTypeLabel = (type: string | null | undefined): string => {
  const key = String(type ?? '').toLowerCase()
  return ASSET_TYPE_LABELS[key] || (type ? String(type) : '-')
}

/** Lifecycle state of an asset, as ResourcesDiscovery reports it. */
const ASSET_STATUS_LABELS: Record<string, string> = {
  active: 'Attivo',
  inactive: 'Inattivo',
  suspended: 'Sospeso',
  deleted: 'Eliminato',
  unknown: 'Sconosciuto'
}

export const assetStatusLabel = (status: string | null | undefined): string => {
  const key = String(status ?? '').toLowerCase()
  return ASSET_STATUS_LABELS[key] || (status ? String(status) : '-')
}

/** Metric a discovered resource carries. */
const RESOURCE_TYPE_LABELS: Record<string, string> = {
  vcpu: 'vCPU',
  ram: 'RAM',
  disk_space: 'Storage',
  s3_single_region: 'S3 Single Region',
  s3_multi_region: 'S3 Multi Region',
  // Acronis
  acronis_storage_c2c_m365: 'Cloud Storage M365/Google',
  acronis_storage_vm: 'Cloud Storage MV',
  acronis_storage_server: 'Cloud Storage Server',
  acronis_storage_workstation: 'Cloud Storage Workstation',
  acronis_storage_s3_public: 'Cloud Storage Pubblico S3',
  acronis_storage_dr: 'Disaster Recovery Storage',
  acronis_edr_endpoints: 'Endpoint protetti EDR',
  acronis_vms_protected: 'Macchine virtuali protette',
  acronis_rmm: 'RMM',
  acronis_dr_cloud_servers: 'Server cloud Disaster Recovery',
  acronis_servers_protected: 'Server protetti',
  acronis_m365_seats: 'Utenze protette M365',
  acronis_vm_direct_public_cloud: 'VM Direct Backup Public Cloud',
  acronis_dlp_workloads: 'Workload DLP protetti',
  acronis_workstations_protected: 'Workstation protette',
  // Cohesity
  cohesity_lic_backup: 'Licenza Backup Cohesity',
  cohesity_lic_replica: 'Licenza Replica Cohesity',
  cohesity_lic_smartfiles: 'Licenza SmartFiles Cohesity',
  cohesity_storage_backup_dc: 'Spazio Backup DC SmeUP',
  cohesity_storage_replica_dc: 'Spazio Replica DC SmeUP',
  license_seat: 'Slot licenza',
  license: 'Licenza',
  slot: 'Slot',
  seat: 'Postazione',
  // ResourcesDiscovery non ha risposto per questa risorsa: il tipo manca, la risorsa no.
  unknown: 'Sconosciuto'
}

/**
 * I nomi dei tipi come li ha il backend (`GET /resource-types`), per i codici
 * che la mappa sopra non conosce: un tipo nuovo, o gli addon Bitdefender, si
 * leggevano come codice grezzo. La mappa resta prima perche' le sue etichette
 * sono corte per le tabelle; quelle del backend sono i nomi lunghi del catalogo.
 * Reattiva: le tabelle gia' a schermo si aggiornano quando arrivano.
 */
const nomiDalBackend = reactive<Record<string, string>>({})

export const registraNomiTipi = (tipi: { resource_type_code: string; name?: string | null }[]): void => {
  for (const t of tipi) {
    if (t.name && t.name !== t.resource_type_code) nomiDalBackend[t.resource_type_code.toLowerCase()] = t.name
  }
}

export const resourceTypeLabel = (type: string | null | undefined): string => {
  const key = String(type ?? '').toLowerCase()
  return RESOURCE_TYPE_LABELS[key] || nomiDalBackend[key] || (type ? String(type) : '-')
}

/** L'unita' di un valore accanto al numero: `count` non e' un'unita', e "2 count" non si legge. */
export const unitLabel = (unit: string | null | undefined): string =>
  !unit || unit === 'count' ? '' : unit

/**
 * Etichetta per i riepiloghi per tipo, dove lo stesso tipo puo' comparire su
 * piu' righe perche' misurato in unita' diverse (Storage in GB e in TB). Due
 * righe "Storage" identiche si leggono come un errore: l'unita' dice perche'
 * sono separate. Si aggiunge solo quando serve davvero.
 */
export const resourceTypeUnitLabel = (
  type: string | null | undefined,
  valueType: string | null | undefined
): string =>
  `${resourceTypeLabel(type)} (${valueType === 'count' || !valueType ? 'conteggio' : valueType})`

/** Lifecycle state of a discovered resource. */
const RESOURCE_STATUS_LABELS: Record<string, string> = {
  active: 'Attiva',
  inactive: 'Inattiva',
  pending: 'In attesa',
  suspended: 'Sospesa',
  deleted: 'Eliminata',
  unknown: 'Sconosciuta'
}

export const resourceStatusLabel = (status: string | null | undefined): string => {
  const key = String(status ?? '').toLowerCase()
  return RESOURCE_STATUS_LABELS[key] || (status ? String(status) : '-')
}

/** What an audit row records. Values come from ActionType on the backend. */
const AUDIT_ACTION_LABELS: Record<string, string> = {
  user_login: 'Accesso',
  user_logout: 'Uscita',
  force_logout: 'Disconnessione forzata',
  user_created: 'Utente creato',
  user_updated: 'Utente aggiornato',
  user_enabled: 'Utente abilitato',
  user_disabled: 'Utente disabilitato',
  user_role_changed: 'Ruolo modificato',
  role_changed: 'Ruolo modificato',
  service_created: 'Servizio creato',
  service_updated: 'Servizio aggiornato',
  service_deleted: 'Servizio eliminato',
  service_archived: 'Servizio archiviato',
  resource_assigned: 'Risorsa assegnata',
  resource_unassigned: 'Risorsa rimossa dal servizio',
  resource_created: 'Risorsa creata',
  resource_updated: 'Risorsa aggiornata',
  resource_deleted: 'Risorsa eliminata',
  resource_type_created: 'Tipo di risorsa creato',
  resource_type_updated: 'Tipo di risorsa aggiornato',
  resource_type_deleted: 'Tipo di risorsa eliminato',
  template_created: 'Template creato',
  template_updated: 'Template aggiornato',
  template_deleted: 'Template eliminato',
  account_created: 'Account creato',
  account_updated: 'Account aggiornato',
  account_deleted: 'Account eliminato',
  api_write: 'Altra modifica'
}

export const auditActionLabel = (action: string | null | undefined): string => {
  const key = String(action ?? '').toLowerCase()
  if (AUDIT_ACTION_LABELS[key]) return AUDIT_ACTION_LABELS[key]
  // An unmapped action still has to read as words, not as SCREAMING_SNAKE.
  return key
    ? key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    : '-'
}

/**
 * Stato di un contratto, scelto dall'operatore: nessuno dei quattro si deduce
 * dalle date. `active` ha gia' la sua pastiglia verde in `StatusPill`; gli
 * altri tre restano grigi perche' dicono tutti "non in vigore", e a
 * distinguerli e' l'etichetta, non il colore.
 */
const CONTRACT_STATUS_LABELS: Record<string, string> = {
  draft: 'Bozza',
  active: 'Attivo',
  terminated: 'Cessato',
  expired: 'Scaduto'
}

export const contractStatusLabel = (status: string | null | undefined): string => {
  const key = String(status ?? '').toLowerCase()
  return CONTRACT_STATUS_LABELS[key] || (status ? String(status) : '-')
}

/** Le stesse quattro voci per i menu a tendina di filtro e di modifica. */
export const CONTRACT_STATUS_OPTIONS = Object.entries(CONTRACT_STATUS_LABELS)
  .map(([value, label]) => ({ value, label }))

export type Tone = 'ok' | 'warn' | 'crit' | 'idle' | 'primary'

/**
 * One place for every state the product shows: service completion, account and
 * asset activity, resource lifecycle. Colour is never the only cue - the dot is
 * paired with the label.
 */
export const STATUS_PILL: Record<string, { tone: Tone; text: string }> = {
  pending: { tone: 'idle', text: 'In attesa' },
  partial: { tone: 'warn', text: 'Parziale' },
  complete: { tone: 'ok', text: 'Completo' },
  archived: { tone: 'primary', text: 'Archiviato' },
  error: { tone: 'crit', text: 'Errore' },
  active: { tone: 'ok', text: 'Attivo' },
  inactive: { tone: 'idle', text: 'Inattivo' },
  deleted: { tone: 'crit', text: 'Eliminato' },
  unknown: { tone: 'warn', text: 'Sconosciuto' },
  online: { tone: 'ok', text: 'Online' },
  running: { tone: 'ok', text: 'In esecuzione' },
  offline: { tone: 'crit', text: 'Offline' },
  stopped: { tone: 'crit', text: 'Fermata' },
  missing: { tone: 'warn', text: 'Mancante' },
  // Esclusione manuale dalla fatturazione: piena se vale questo mese, spenta
  // se i suoi mesi sono passati o devono venire.
  non_fatturabile: { tone: 'warn', text: 'Non fatturabile' },
  non_fatturabile_altri_mesi: { tone: 'idle', text: 'Non fatturabile' },
  // Disdetta (OPENSPEC_DISDETTE): ancora da venire, oppure gia' cessato.
  in_disdetta: { tone: 'warn', text: 'In disdetta' },
  cessato: { tone: 'crit', text: 'Cessato' }
}

/** Il testo che StatusPill mostra per uno stato. */
export const statusPillText = (status: string | null | undefined): string =>
  STATUS_PILL[String(status ?? '').toLowerCase()]?.text ?? status ?? '-'
